<script lang="ts">
  import type {Video} from '@/types/project';
  import Swal from 'sweetalert2';
  import {httpClient} from '@/utils/http-client';
  import {onDestroy, onMount} from 'svelte';
  import {savedBlobs, showAudioDownloadIndicator} from './Audio';
  import {isMobile} from '@/store';

  export let selectedVideo: Video | null;

  let isLoading = true;
  let audioURL = '';

  let downloadStatus: '' | 'downloading' | 'complete' | 'error' = '';

  const showAudioDownloadIndicatorSubscription = showAudioDownloadIndicator.subscribe(value => {
    downloadStatus = value;
  });

  const download = async () => {
    if (downloadStatus === 'downloading') return;
    showAudioDownloadIndicator.set('downloading');

    const urlSplit = audioURL.split('.');
    const extension = urlSplit[urlSplit.length - 1];

    if (typeof audioURL !== 'string' || audioURL === '') {
      showAudioDownloadIndicator.set('error');
      return alert('audio url is invalid.');
    }

    const savedBlob = savedBlobs.get(audioURL);

    if (savedBlob) {
      const blobURL = URL.createObjectURL(savedBlob);
      const a = document.createElement('a');
      a.target = '_blank';
      a.href = blobURL;
      a.download = `${selectedVideo?.title || 'sermonshots-audio'}.${extension}`;
      a.click();
      showAudioDownloadIndicator.set('complete');
      return;
    }

    fetch(audioURL)
      .then(response => response.blob())
      .then(blob => {
        savedBlobs.set(audioURL, blob);
        const blobURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.target = '_blank';
        a.href = blobURL;
        a.download = `${selectedVideo?.title || 'sermonshots-audio'}.${extension}`;
        a.click();
        showAudioDownloadIndicator.set('complete');
      })
      .catch(error => {
        console.error(error);
        showAudioDownloadIndicator.set('error');
      });
  };

  onMount(() => {
    if (!selectedVideo) {
      console.error('selected video is null');
      isLoading = false;
      return;
    }

    httpClient
      .get(`/audio?videoid=${selectedVideo.id}`)
      .then((response: any) => {
        const audioUrl = response.data?.audioUrl || '';
        if (typeof audioUrl !== 'string') throw new Error('Audio url is invalid');
        audioURL = audioUrl;
        isLoading = false;
      })
      .catch(error => {
        console.error(error);
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Unable to Download Audio',
          showConfirmButton: false,
          timer: 1500,
          toast: true
        });
        isLoading = false;
      });
  });

  onDestroy(showAudioDownloadIndicatorSubscription);
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">Audio</h2>
    {/if}
    <div class="ml-auto flex w-full items-center lg:w-auto">
      {#if downloadStatus === 'downloading'}
        <button
          on:click={() => showAudioDownloadIndicator.set(downloadStatus)}
          class="flex w-full items-center justify-center rounded-md bg-rose-500/50 px-3 py-2 font-medium text-zinc-100 lg:w-auto"
          style="min-width: 128px;"
          disabled={true}
        >
          <i class="fa-solid fa-spinner rotating"></i>
        </button>
      {:else}
        <button
          on:click={() => !isLoading && audioURL !== '' && download()}
          class="px-3 py-1 font-medium text-zinc-100 {isLoading || audioURL === ''
            ? 'bg-zinc-400'
            : 'hoversermonbg bg-rose-500'} w-full rounded-md lg:w-auto"
          disabled={isLoading || audioURL === ''}
        >
          Download
        </button>
      {/if}
    </div>
  </div>
  <div class="flex h-[calc(100%-4rem)] w-full flex-col items-center p-4">
    {#if isLoading}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          ></div>
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Audio...</h1>
        </div>
      </div>
    {:else if audioURL !== ''}
      <div class="h-full w-full max-w-[40rem]">
        <div class="w-full py-4">
          <audio class="w-full" src={audioURL} controls={true}></audio>
        </div>
        <div class="grid w-full gap-8 pb-16 pt-8">
          <h1 class="mb-2 text-3xl font-bold">F.A.Qs</h1>
          <div>
            <h1 class="text-2xl font-bold">Why would I want to convert my sermon videos to audio?</h1>
            <p>
              Converting sermon videos to audio offers a versatile listening experience, perfect for members of your
              congregation who are often on the move. Whether they're driving, exercising, or simply prefer audio
              content, this feature ensures they are able to listen to your sermon, without the need to watch a video.
            </p>
          </div>
          <div>
            <h1 class="text-2xl font-bold">How can my congregation access the audio sermons?</h1>
            <p>
              Through popular podcast apps or directly on your church's website. This makes it convenient, using
              platforms they're already familiar with.
            </p>
          </div>
          <div>
            <h1 class="text-2xl font-bold">
              Where do I upload it so my audio shows up on podcast apps (Apple, Spotify, etc)?
            </h1>
            <p>To share your audio sermons on platforms like Spotify and Apple Podcasts, follow these steps:</p>
            <ol class="list-decimal py-4 pl-4">
              <li>Select a podcast hosting service.</li>
              <li>Upload your sermon's audio file to this service.</li>
              <li>Provide your hosting service's RSS feed URL when adding a new episode on these platforms.</li>
            </ol>
            <p>
              This process ensures your audio content seamlessly appears where your congregation is already listening.
            </p>
          </div>
          <div>
            <h1 class="text-2xl font-bold">Can I put this on my website?</h1>
            <p>Absolutely! Integrating audio sermons on your website is straightforward. You can use:</p>
            <ul class="list-disc py-4 pl-4">
              <li>A simple HTML5 audio tag for basic playback.</li>
              <li>A third-party hosting service that offers an embed code for a more polished look.</li>
              <li>
                Built-in audio players provided by platforms like WordPress and <a
                  href="https://help.thechurchco.com/en/articles/5072992-adding-a-new-sermon">The Church Co.</a
                >, which are specifically designed for ease of use.
              </li>
            </ul>
            <p>
              Choosing the right method depends on your comfort with technology and how you'd like your congregation to
              engage with your content.
            </p>
          </div>
        </div>
      </div>
    {:else}
      <div>
        <h1>No Audio</h1>
      </div>
    {/if}
  </div>
</div>
